import cx from 'clsx';
import { format } from 'date-fns';
import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TableLoading from '@components/common/TableLoading/TableLoading';
import TableLoadingTrigger from '@components/common/TableLoadingTrigger/TableLoadingTrigger';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslation from '@hooks/useTranslation';

import { archiveActionItemsTooltipIds } from '@pages/Archive/constants/archiveActionItemsTooltipIds';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { getComponentName } from '../../helpers/getComponentName';
import type { IArchivedActionItem, IArchivedAiStore } from '../../model/archivedAi';
import { selectArchiveFiltersData, useArchiveFiltersActions } from '../../store/useArchiveFiltersStore';
import ArchiveFooter from '../ArchiveFooter/ArchiveFooter';
import ArchiveTableSkeleton from './ArchiveTableSkeleton';
import style from './style.module.scss';

interface IProps {
  isLoading: boolean;
  isFetching: boolean;
  fetchNextPage?: () => void;
  archivedAiList?: IArchivedActionItem[];
  totalCount: number | undefined;
}

const ArchiveTable: React.FC<IProps> = ({
  isLoading,
  isFetching,
  fetchNextPage,
  archivedAiList = [],
  totalCount,
}) => {
  const archiveAiTableContainerRef = useRef(null);

  const { t } = useTranslation();
  const { customerId } = useParams();
  const { pathname } = useLocation();

  const locationSelectValue = selectAppLocationSelectValue();
  const { sorting }: IArchivedAiStore = selectArchiveFiltersData();
  const { setArchiveFiltersKey } = useArchiveFiltersActions();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const { data: profileData } = useUserProfileQuery();
  const { data: assetTreeData } = useAssetTreeQuery();

  const [ref, isInViewPort] = useIntersectionObserver({
    threshold: 0.1,
    reInitializeOnChange: isFetching,
  });

  useEffect(() => {
    if (isInViewPort && fetchNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [isInViewPort, fetchNextPage, isFetching]);

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setArchiveFiltersKey('sorting', { id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setArchiveFiltersKey('sorting', initialSorting);
    } else {
      setArchiveFiltersKey('sorting', { id, desc: true });
    }
  };

  const getTimeBetween = (lifetime: number) => {
    if (lifetime < 24) {
      return `${lifetime} ${lifetime > 1 || lifetime === 0 ? t('Hours') : t('hour')}`;
    }

    const lifetimeDays = Math.floor(lifetime / 24);

    return `${lifetimeDays} ${lifetimeDays > 1 ? t('days') : t('day')}`;
  };

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  return (
    <div>
      <div className={style.archivedAiTableWrapper} ref={archiveAiTableContainerRef}>
        <table className={style.archivedAiTable}>
          <thead>
            <tr>
              <th>
                {t('Location')}
                <TableSort
                  className={cx(style.archivedAiTableSortIcon, {
                    [style.activeSort]: sorting.id === 'locationName',
                  })}
                  onClick={() => onChangeSorting('locationName')}
                  data-tooltip-content="locationName"
                  data-tooltip-id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID}
                />
              </th>
              <th>
                <div className={style.assetColumn}>
                  <div className={style.hideColumn}>{t('Asset')}</div>
                  <div className={style.showColumn}>
                    <div>{t('Asset')} / </div>
                    <div>{t('Component')}</div>
                  </div>
                  <TableSort
                    className={cx(style.archivedAiTableSortIcon, {
                      [style.activeSort]: sorting.id === 'equipmentName',
                    })}
                    onClick={() => onChangeSorting('equipmentName')}
                    data-tooltip-content="equipmentName"
                    data-tooltip-id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID}
                  />
                </div>
              </th>
              <th className={style.hideColumn}>
                {t('Component')}
                <TableSort
                  className={cx(style.archivedAiTableSortIcon, {
                    [style.activeSort]: sorting.id === 'componentName',
                  })}
                  onClick={() => onChangeSorting('componentName')}
                  data-tooltip-content="componentName"
                  data-tooltip-id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID}
                />
              </th>
              <th>
                {t('Type')}
                <TableSort
                  className={cx(style.archivedAiTableSortIcon, {
                    [style.activeSort]: sorting.id === 'typeName',
                  })}
                  onClick={() => onChangeSorting('typeName')}
                  data-tooltip-content="typeName"
                  data-tooltip-id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID}
                />
              </th>
              <th className={style.alignRightColumn}>
                {t('Lifetime')}
                <TableSort
                  className={cx(style.archivedAiTableSortIcon, {
                    [style.activeSort]: sorting.id === 'lifetime',
                  })}
                  onClick={() => onChangeSorting('lifetime')}
                  data-tooltip-content="lifetime"
                  data-tooltip-id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID}
                />
              </th>
              {customerInfoData?.showUnplannedDowntime && (
                <th className={cx(style.alignRightColumn, style.hideColumn)}>
                  <span
                    data-tooltip-content={`${t('Unplanned downtime avoided')} (${t('hrs')})`}
                    data-tooltip-id={archiveActionItemsTooltipIds.CELL_TOOLTIP_ID}
                  >
                    {t('UDT hours')}
                  </span>
                  <TableSort
                    className={cx(style.archivedAiTableSortIcon, {
                      [style.activeSort]: sorting.id === 'downtimeAvoided',
                    })}
                    onClick={() => onChangeSorting('downtimeAvoided')}
                    data-tooltip-content="downtimeAvoided"
                    data-tooltip-id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID}
                  />
                </th>
              )}
              {customerInfoData?.showTotalCostSavings && (
                <th className={style.alignRightColumn}>
                  <div className={style.totalCostAvoidedColumn}>
                    <div>
                      {t('Total cost avoided')}, {currentCurrency.symbol}
                    </div>
                    <TableSort
                      className={cx(style.archivedAiTableSortIcon, {
                        [style.activeSort]: sorting.id === 'costAvoided',
                      })}
                      onClick={() => onChangeSorting('costAvoided')}
                      data-tooltip-content="costAvoided"
                      data-tooltip-id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID}
                    />
                  </div>
                </th>
              )}
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <ArchiveTableSkeleton />
            ) : archivedAiList.length ? (
              archivedAiList.map((row) => {
                const componentName = getComponentName(row, t);
                return (
                  <Link
                    key={row.id}
                    className={style.rowLink}
                    to={`/customer/${customerId}/ai/${row.id}`}
                    state={{ prevPage: pathname }}
                  >
                    <tr className={style.archivedAiBodyRow}>
                      <td>
                        <div
                          className={style.ellipsisTextThreeLines}
                          data-tooltip-content={row.locationName}
                          data-tooltip-id={archiveActionItemsTooltipIds.CELL_TOOLTIP_ID}
                        >
                          {row.locationName}
                        </div>
                        {(locationSelectValue.id === 'all' || locationSelectValue.level_name === 'group') && (
                          <div
                            className={style.facilityName}
                            data-tooltip-content={row.facilityName}
                            data-tooltip-id={archiveActionItemsTooltipIds.CELL_TOOLTIP_ID}
                          >
                            {row.facilityName}
                          </div>
                        )}
                      </td>
                      <td>
                        <div
                          className={style.ellipsisTextThreeLines}
                          data-tooltip-content={row.equipmentName}
                          data-tooltip-id={archiveActionItemsTooltipIds.CELL_TOOLTIP_ID}
                        >
                          {row.equipmentName}
                        </div>
                        <div
                          className={style.componentName}
                          data-tooltip-content={componentName}
                          data-tooltip-id={archiveActionItemsTooltipIds.CELL_TOOLTIP_ID}
                        >
                          {componentName}
                        </div>
                      </td>
                      <td className={style.hideColumn}>{componentName}</td>
                      <td>{t(row.typeName)}</td>
                      <td className={style.alignRightColumn}>
                        {getTimeBetween(+row.lifetime)}
                        <div className={style.lifetimeRange}>
                          {`${format(
                            row.timeCreated,
                            profileData?.dateFormat.date.slice(0, -2) || '',
                          )} - ${format(row.closedAt, profileData?.dateFormat.date.slice(0, -2) || '')}`}
                        </div>
                      </td>
                      {customerInfoData?.showUnplannedDowntime && (
                        <td className={cx(style.alignRightColumn, style.hideColumn)}>
                          {row.downtimeAvoided}
                        </td>
                      )}
                      {customerInfoData?.showTotalCostSavings && (
                        <td className={style.alignRightColumn}>
                          {isSelectedAllFacility
                            ? row.costAvoidedCustomerCurrency
                            : row.costAvoidedFacilityCurrency}
                        </td>
                      )}
                      <td>
                        {row.commentsCount ? (
                          <div
                            className={style.commentsCircle}
                            data-tooltip-content={t('Comments count')}
                            data-tooltip-id={archiveActionItemsTooltipIds.CELL_TOOLTIP_ID}
                          >
                            {row.commentsCount}
                          </div>
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                  </Link>
                );
              })
            ) : (
              <TableEmptyRowExtended text={t('No archived action items were found.')} />
            )}
          </tbody>
          <tfoot>
            <TableLoadingTrigger show={!isFetching} ref={ref} />
            <TableLoading show={isFetching && !isLoading} />
          </tfoot>
        </table>
        <Tooltip id={archiveActionItemsTooltipIds.CELL_TOOLTIP_ID} place="top-start" />
        <TooltipSort id={archiveActionItemsTooltipIds.SORT_TOOLTIP_ID} sorting={sorting} />
        <Tooltip id={archiveActionItemsTooltipIds.LONG_TEXT_TOOLTIP_ID} className={style.tooltipSizeMedium} />
      </div>
      <ArchiveFooter
        archiveAiTableContainerRef={archiveAiTableContainerRef}
        isLoading={isLoading}
        displayedArchivedAiCount={archivedAiList.length}
        totalCount={totalCount}
        sorting={sorting}
      />
    </div>
  );
};

export default ArchiveTable;
